/**
 * One state vocabulary and one copy set for doctor case visibility (MDRS-230).
 *
 * The same three states and the same release explanation appear on the cases
 * list, the Create/Update case modals and the case detail Access card. Keeping
 * them here stops the wording drifting between surfaces — the earlier build had
 * four different phrasings for two states.
 */

/**
 * `user_type`s that can be assigned to a case as its doctor.
 *
 * Mirrors the backend's `_DOCTOR_USER_TYPES` (core/authz.py) — both `expert` and
 * `internal` get the full doctor app, so both are legitimate case owners.
 *
 * Deliberately WIDER than the set the doctor-ready gate applies to
 * (`_DOCTOR_READY_GATED_USER_TYPES` = `expert` only): internal staff do the prep
 * work, so they see their cases without needing a release. "Who can own a case"
 * and "who gets gated" are separate questions — conflating them left the picker
 * empty on data where every user is `internal`.
 */
const ASSIGNABLE_DOCTOR_TYPES = ["expert", "internal"] as const;

export const isAssignableDoctor = (userType?: string | null): boolean =>
  ASSIGNABLE_DOCTOR_TYPES.includes(
    (userType ?? "").trim().toLowerCase() as (typeof ASSIGNABLE_DOCTOR_TYPES)[number]
  );

export const DOCTOR_ACCESS_STATE = {
  RELEASED: "Released",
  HIDDEN: "Hidden",
  UNASSIGNED: "Unassigned",
} as const;

export type DoctorAccessState =
  (typeof DOCTOR_ACCESS_STATE)[keyof typeof DOCTOR_ACCESS_STATE];

/**
 * Resolve the visibility state of a case. "Unassigned" wins over "Hidden": with
 * no doctor there is nobody for the flag to release it to, so reporting "Hidden"
 * would imply a doctor is waiting on it.
 */
export const doctorAccessState = (
  hasDoctor: boolean,
  isDoctorReady?: boolean | null
): DoctorAccessState => {
  if (!hasDoctor) return DOCTOR_ACCESS_STATE.UNASSIGNED;
  return isDoctorReady ? DOCTOR_ACCESS_STATE.RELEASED : DOCTOR_ACCESS_STATE.HIDDEN;
};

export const DOCTOR_ACCESS_COPY = {
  /** Card/section titles. */
  accessTitle: "Access",
  /** Case detail card — autosaves, so the subtitle says so. */
  accessSubtitle:
    "Who this case belongs to and who can see it. Changes save automatically.",
  /** Update modal card — saved on submit, so no autosave promise. */
  accessCardHint: "Who this case belongs to and who can see it.",

  /** Assigned-doctor control. */
  doctorLabel: "Assigned doctor",
  doctorHint:
    "The doctor this case belongs to. Reassigning will not move files that are already uploaded.",
  doctorPlaceholder: "No doctor assigned",
  doctorUnassigned: "Not assigned",

  /** Card descriptions on the Create/Update modals. */
  examineeCardTitle: "Examinee and claim",
  examineeCardHint: "Who this case is about.",
  assessmentCardTitle: "Assessment",
  assessmentCardHint: "What is being assessed and which report comes out of it.",
  scheduleCardTitle: "Schedule",
  scheduleCardHint: "When the examinee is seen and when the report is owed.",

  /** Field-level hints. */
  serviceTypeHint: "Sets the report template and the Assessment Scribe sections.",

  /** Ready-for-doctor toggle. */
  readyLabel: "Ready for doctor",
  /** Shown when the case is released. */
  readyNoteReleased: "The assigned doctor can see this case and its documents.",
  /** Shown when a doctor is assigned but the case is still hidden. */
  readyNoteHidden:
    "Hidden from the assigned doctor until you release it. Release once the bundle has been split, categorised and reviewed.",
  /** Shown when there is no doctor to release to. */
  readyNoteUnassigned: "Assign a doctor before releasing this case.",
  /** New-case note, shown under the doctor picker on Create. */
  createHint: "New cases start hidden. You can release the case once it's ready.",

  /** Referrer control. */
  referrerLabel: "Referrer",
  referrerHint:
    "Referrers get read-only access. They cannot create cases, this is how they get one.",
  referrerPlaceholder: "No referrer",

  /** Confirmation dialog — release. */
  confirmReleaseTitle: "Release case to the doctor?",
  confirmReleaseBody:
    "The assigned doctor will be able to see this case and all of its documents.",
  confirmReleaseAction: "Release case",

  /** Confirmation dialog — hide. */
  confirmHideTitle: "Hide case from the doctor?",
  confirmHideBody:
    "The case and its documents will no longer appear for the assigned doctor.",
  confirmHideAction: "Hide case",

  cancel: "Cancel",
  saved: "Saved",
} as const;

/**
 * The note under the Ready-for-doctor toggle, for the current state.
 *
 * Pass `doctorName` to name the doctor rather than saying "the assigned doctor" —
 * "Dr Elena Marsh cannot open this case yet" tells an admin exactly who is
 * waiting, which is the point of the row.
 */
export const readyNoteFor = (
  state: DoctorAccessState,
  doctorName?: string | null
): string => {
  if (state === DOCTOR_ACCESS_STATE.UNASSIGNED)
    return DOCTOR_ACCESS_COPY.readyNoteUnassigned;

  if (!doctorName) {
    return state === DOCTOR_ACCESS_STATE.RELEASED
      ? DOCTOR_ACCESS_COPY.readyNoteReleased
      : DOCTOR_ACCESS_COPY.readyNoteHidden;
  }

  return state === DOCTOR_ACCESS_STATE.RELEASED
    ? `${doctorName} can open this case and its documents.`
    : `${doctorName} cannot open this case yet. Turn on when the bundle is ready for them.`;
};
